import { useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    PageHeader,
    PublicId,
    StatusBadge,
    TagList,
} from '@bemor/web-kit/ui';

import type {
    Merchant,
    PaymentSystemCatalogue,
    ProviderAccount,
} from '@/api/types';
import { TagFilter } from '@/components/tag-filter';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';

const PATH = '/staff/merchants';
const PAGE_KEY = 'merchants';

/** The columns, keyed like the export's columns (so Export writes what the table shows). */
export const merchantColumns: ListColumnDef<Merchant>[] = [
    {
        key: 'display_name',
        aliases: ['name'],
        label: 'Merchant',
        sortKey: 'display_name',
        cell: (m) => (
            <span>
                {m.display_name || m.legal_name ? (
                    <>
                        <span className="font-medium">{m.display_name}</span>
                        <span className="text-muted-foreground ml-2 text-xs">
                            {m.legal_name}
                        </span>
                    </>
                ) : (
                    // a draft merchant has no name until onboarding gives it one
                    <span className="text-muted-foreground">No name yet</span>
                )}
            </span>
        ),
        csv: (m) => m.display_name,
        locked: 'start',
        boardCard: true,
    },
    {
        key: 'id',
        label: 'Id',
        cell: (m) => <PublicId id={m.id} />,
        csv: (m) => m.id,
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (m) => <StatusBadge status={m.status} domain="merchant" />,
        csv: (m) => m.status,
        groupable: { key: 'status', statusDomain: 'merchant' },
    },
    {
        key: 'kyc_status',
        aliases: ['kyc'],
        label: 'KYC',
        sortKey: 'kyc_status',
        cell: (m) => <StatusBadge status={m.kyc_status} domain="kyc" />,
        csv: (m) => m.kyc_status,
        groupable: { key: 'kyc_status', statusDomain: 'kyc' },
        boardCard: true,
    },
    {
        key: 'created_at',
        aliases: ['created'],
        label: 'Since',
        kind: 'datetime',
        sortKey: 'created_at',
        cell: (m) => <DateTime value={m.created_at} format="date" empty="—" />,
        csv: (m) => m.created_at,
        noTruncate: true,
    },
    {
        key: 'tags',
        label: 'Tags',
        cell: (m) => <TagList tags={m.tags ?? []} />,
        csv: (m) => (m.tags ?? []).map((t) => t.name).join(', '),
        defaultHidden: true,
    },
];

export const merchantFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    kyc_status: 'KYC status',
    tag: 'Tags',
    provider: 'Provider',
    payment_system: 'Payment system',
    provider_account_id: 'Provider account',
    range: 'Created',
});

/**
 * BM-111 § 1: the provider, payment system and provider account choices, from the code-owned catalogue and the
 * tenant's provider accounts. Both reads need payments.systems.manage, so without it the three controls are dropped.
 */
function useLinkFilterControls() {
    const allowed = useAuthStore((s) => s.can)(P.systemsManage);
    const catalogue = useOne<PaymentSystemCatalogue>(
        allowed ? '/staff/payment-systems' : null,
    ).data?.data;
    const accounts = useOne<ProviderAccount[]>(
        allowed ? '/staff/provider-accounts' : null,
    ).data?.data;
    if (!allowed)
        return {
            provider: null,
            payment_system: null,
            provider_account_id: null,
        };
    return {
        provider: {
            kind: 'enum',
            options: (catalogue?.providers ?? []).map((p) => ({
                value: p.code,
                label: p.display_name,
            })),
        },
        payment_system: {
            kind: 'enum',
            options: optionsOf(
                (catalogue?.systems ?? []).map((s) => s.payment_system),
            ),
        },
        provider_account_id: {
            kind: 'enum',
            options: (accounts ?? []).map((a) => ({
                value: String(a.id),
                label: a.name,
            })),
        },
    } as const;
}

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const merchantsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

export function MerchantsPage() {
    const { state, query } = useAdminList<Merchant>(PAGE_KEY, PATH, {
        columns: merchantColumns,
        filters: merchantFilters,
    });
    const linkControls = useLinkFilterControls();
    return (
        <div>
            <PageHeader
                title="Merchants"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Every merchant of the tenant; review and approve from the detail page.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="merchants"
                state={state}
                query={query}
                rowKey={(m) => m.id}
                rowHref={(m) => `/merchants/${m.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'merchant' },
                    kyc_status: { domain: 'kyc' },
                    tag: <TagFilter state={state} />,
                    ...linkControls,
                }}
            />
        </div>
    );
}
